// The GPU fleet board.
//
// "units" gets a real board rather than a generic table: it is the tab
// that answers "what am I paying for right now", the states are a small
// known vocabulary (internal/compute/types.go: STARTING, READY, STOPPING,
// STOPPED, FAILED), and the unit struct carries worker credentials that
// must not be rendered (api/admin.ts ComputeUnitRaw). The remaining tabs
// are raw diagnostics -- assignments, leases, stats and deployments change
// shape with the compute layer, and a generic table stays correct as they
// evolve instead of silently dropping fields.
import { useCallback, useEffect, useState } from "react";
import { fetchComputeUnits, type ComputeUnitRaw } from "../../api/admin";
import { proxyCompute } from "../../api/client";
import JsonTable from "../../components/JsonTable";
import { boardUnits, describeError, describeRaw, gpuTime, isGpuUnit, liveGpuUnits, liveUnits, stateClass, tallyStates, unitTone } from "../../lib/fleet";
import { formatMinor, formatSince, formatWhen } from "../../lib/format";

const TABS = ["units", "assignments", "leases", "stats", "deployments"] as const;
type Tab = (typeof TABS)[number];
const REFRESH_MS = 10000;
const WEEK_MS = 7 * 24 * 3_600_000;

export default function AdminCompute() {
  const [tab, setTab] = useState<Tab>("units");
  const [data, setData] = useState<unknown>(undefined);
  const [units, setUnits] = useState<ComputeUnitRaw[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [at, setAt] = useState<Date | null>(null);
  const [all, setAll] = useState(false);

  const load = useCallback(async () => {
    try {
      if (tab === "units") {
        const u = await fetchComputeUnits();
        setUnits(u);
      } else {
        setData(await proxyCompute<unknown>(tab));
      }
      setAt(new Date());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load compute state");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const board = units ? boardUnits(units, all) : [];
  const tally = tallyStates(board);
  const live = units ? liveGpuUnits(units) : [];
  const llm = units ? liveUnits(units).filter((u) => !isGpuUnit(u)).length : 0;
  const week = units ? gpuTime(units, WEEK_MS) : null;

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">GPU fleet</h1>
          <p className="a-lede">
            Live units first, then the last week (or every unit Compute knows about). A GPU unit that
            is not STOPPED or FAILED is holding a provider resource, whether or not it is serving.
          </p>
        </div>
        <span className="a-panel__meta">{at ? `read ${at.toLocaleTimeString()}` : "reading…"}</span>
      </div>

      <div className="a-tabs">
        {TABS.map((t) => (
          <button key={t} className={t === tab ? "a-tab a-tab--on" : "a-tab"} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {error && <p className="a-error">{error}</p>}
      {loading ? (
        <p className="a-loading">Loading…</p>
      ) : tab === "units" ? (
        <>
          <div className="a-tiles">
            {/* Live units is not a state -- it is the spend reading, and it
                is the one number here that answers "what am I paying for".
                Every other tile is one state from the tally. A separate
                "Failed" tile was here and was pure duplication: its label
                and the tally's FAILED row rendered identically once
                .a-tile__label's uppercase landed on both. */}
            <div className="a-tile">
              <span className="a-tile__label">Live GPUs</span>
              <span className="a-tile__value a-tile__value--cyan">{live.length}</span>
              <span className="a-tile__sub">
                holding resources{llm > 0 ? ` · +${llm} LLM API (no GPU)` : ""}
              </span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">GPU time, 7 d</span>
              <span className="a-tile__value">{week ? `${week.hours.toFixed(1)} h` : "—"}</span>
              <span className="a-tile__sub">{week ? `${week.units} units · ~${formatMinor(week.listCostMinor, "USD_CENTS")} at list price` : "—"}</span>
            </div>
            {tally.map((t) => (
              <div className="a-tile" key={t.state}>
                <span className="a-tile__label">{t.state.toLowerCase()}</span>
                <span
                  className={`a-tile__value a-tile__value--${
                    t.tone === "ok" ? "green" : t.tone === "busy" ? "amber" : t.tone === "bad" ? "red" : "cyan"
                  }`}
                >
                  {t.count}
                </span>
                <span className="a-tile__sub">
                  {t.tone === "ok" ? "serving" : t.tone === "busy" ? "transitional" : t.tone === "bad" ? "needs attention" : "parked"}
                </span>
              </div>
            ))}
          </div>

          <p className="a-note">
            {all ? `All ${units?.length ?? 0} units Compute knows about.` : `Live units and the last 7 days: ${board.length} of ${units?.length ?? 0}.`}{" "}
            <button className="a-btn" onClick={() => setAll((v) => !v)}>
              {all ? "Last 7 days only" : "Show all"}
            </button>
          </p>
          {board.length === 0 ? (
            <p className="a-empty">No compute units{all ? "" : " in the last 7 days"}. Nothing is being billed.</p>
          ) : (
            <div className="a-fleet">
              {board.map((u, i) => {
                const busy = unitTone(u.State) === "busy";
                return (
                  <div className={`a-cu a-cu--${cuClass(unitTone(u.State))}`} key={u.ID ?? i}>
                    <div className="a-cu__top">
                      <span className={`a-lamp a-lamp--${lampFor(unitTone(u.State))}`} />
                      <span className="a-cu__id">{u.ID ? u.ID.slice(0, 8) : "—"}</span>
                      <span className={`a-cu__state a-state--${stateClass(u.State)}`}>
                        {(u.State ?? "unknown").toLowerCase()}
                      </span>
                    </div>
                    <div className="a-cu__rows">
                      <span>runtime</span>
                      <span>{u.Runtime ?? "—"}</span>
                      <span>deployment</span>
                      <span className="a-table__hi">{u.Deployment ?? "—"}</span>
                      <span>backend</span>
                      <span>
                        {u.Backend ?? "—"}
                        {u.Offering ? ` · ${u.Offering}` : ""}
                      </span>
                      <span>hardware</span>
                      <span>{describeRaw(u.Hardware)}</span>
                      <span>pricing</span>
                      <span>{describeRaw(u.Pricing)}</span>
                      <span>created</span>
                      <span>
                        {formatWhen(u.CreatedAt)} · {formatSince(u.CreatedAt)}
                      </span>
                      {u.ReadyAt && (
                        <>
                          <span>ready</span>
                          <span>{formatWhen(u.ReadyAt)}</span>
                        </>
                      )}
                      <span>heartbeat</span>
                      <span className={staleHeartbeat(u.LastHeartbeatAt) ? "a-table__hi" : undefined}>
                        {u.LastHeartbeatAt ? formatSince(u.LastHeartbeatAt) : "—"}
                      </span>
                      {u.IdleSince && (
                        <>
                          <span>idle</span>
                          <span className={unitTone(u.State) === "ok" ? "a-table__hi" : undefined}>
                            {formatSince(u.IdleSince)}
                          </span>
                        </>
                      )}
                      {u.Error && (
                        <>
                          <span>error</span>
                          <span className="a-table__hi" title={u.Error.source}>
                            {describeError(u.Error)}
                          </span>
                        </>
                      )}
                      {u.StoppedAt && (
                        <>
                          <span>stopped</span>
                          <span>{formatWhen(u.StoppedAt)}</span>
                        </>
                      )}
                      {u.FailedAt && (
                        <>
                          <span>failed</span>
                          <span className="a-table__hi">{formatWhen(u.FailedAt)}</span>
                        </>
                      )}
                    </div>
                    {busy && <p className="a-note">{busyNote(u)}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div className="a-panel">
          <div className="a-panel__head">
            <span className="a-panel__title">{tab}</span>
            <span className="a-panel__meta">raw Compute response</span>
          </div>
          <JsonTable data={data} />
        </div>
      )}
    </>
  );
}

/** Card modifiers are named after units, not after tones: a serving unit
 * is "ready", a dead one "failed" (both are compute's own words). */
function cuClass(tone: string): string {
  if (tone === "ok") return "ready";
  if (tone === "bad") return "failed";
  return tone;
}

function lampFor(tone: string): string {
  if (tone === "ok") return "green";
  if (tone === "busy") return "amber";
  if (tone === "bad") return "red";
  return "cyan";
}

/** A READY unit with no recent heartbeat is not serving anyone; the
 * heartbeat column highlights rather than the whole card, so the fleet
 * stays scannable. */
function staleHeartbeat(iso?: string): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return !Number.isNaN(t) && Date.now() - t > 120_000;
}

function busyNote(u: ComputeUnitRaw): string {
  const since = u.CreatedAt ? ` for ${formatSince(u.CreatedAt)}` : "";
  if ((u.State ?? "").toUpperCase() === "STARTING") {
    return `Starting${since}: billing, not yet serving.`;
  }
  return `Stopping${since}: releasing the provider resource.`;
}
